import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { SERVICES } from "@/data/site";
import * as Lucide from "lucide-react";
import { ArrowRight, Check, Sprout, Hammer, Paintbrush, Home as HomeIcon, LayoutGrid } from "lucide-react";

const Icon = ({ name, ...p }) => { const C = Lucide[name] || Lucide.Leaf; return <C {...p}/>; };

const CATEGORIES = [
  { key: "All", label: "All", icon: LayoutGrid },
  { key: "Garden", label: "Garden", icon: Sprout },
  { key: "Joinery", label: "Joinery", icon: Hammer },
  { key: "Painting", label: "Painting", icon: Paintbrush },
  { key: "Property", label: "Property", icon: HomeIcon },
];

export default function Services() {
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return SERVICES;
    return SERVICES.filter((s) => (s.category || "Garden") === active);
  }, [active]);

  const countFor = (key) => key === "All" ? SERVICES.length : SERVICES.filter((s) => (s.category || "Garden") === key).length;

  return (
    <div data-testid="services-page" className="pt-32">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-10">
        <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ Services</div>
        <h1 className="font-display uppercase tracking-tighter text-5xl md:text-7xl text-[#F0F4F1] max-w-4xl">
          Pick a service, <span className="text-[#8DE038]">book a pro.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#9CAEA3] leading-relaxed">
          Browse what&apos;s on offer. All work delivered by vetted local contractors. You pay them directly in cash or bank transfer — Green Connect just takes a small platform fee.
        </p>

        {/* Category filter chips */}
        <div data-testid="services-category-filter" className="mt-9 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const isActive = active === c.key;
            const count = countFor(c.key);
            return (
              <button
                key={c.key}
                data-testid={`category-chip-${c.key.toLowerCase()}`}
                onClick={() => setActive(c.key)}
                aria-pressed={isActive}
                className={`group inline-flex items-center gap-2 px-4 py-2.5 border transition-colors font-display uppercase tracking-widest text-xs ${
                  isActive
                    ? "border-[#8DE038] bg-[#8DE038] text-[#050806]"
                    : "border-[#1F3325] text-[#C8D4CB] hover:border-[#8DE038] hover:text-[#8DE038]"
                }`}
              >
                <c.icon size={14}/>
                <span>{c.label}</span>
                <span className={`text-[10px] px-1.5 py-0.5 ${isActive ? "bg-[#050806] text-[#8DE038]" : "bg-[#0F1712] text-[#9CAEA3]"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="section-pad bg-[#050806] pt-0">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {filtered.length === 0 ? (
            <div data-testid="services-empty" className="text-center py-20 text-[#9CAEA3]">No services in this category yet.</div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-5">
              {filtered.map((s) => (
                <div data-testid={`service-card-${s.slug}`} key={s.slug} className="gl-card p-8 md:p-10 flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-[#8DE038]/10 border border-[#8DE038]/40 flex items-center justify-center text-[#8DE038]">
                      <Icon name={s.icon} size={26}/>
                    </div>
                    {s.category && (
                      <span className="font-display uppercase tracking-widest text-[10px] text-[#8DE038] border border-[#8DE038]/40 px-2 py-1">{s.category}</span>
                    )}
                  </div>
                  <div className="font-display uppercase tracking-wider text-3xl text-[#F0F4F1]">{s.name}</div>
                  <p className="text-[#9CAEA3] mt-3 leading-relaxed">{s.short}</p>
                  <ul className="mt-6 space-y-2">
                    {s.rows.slice(0, 5).map(([label, price]) => (
                      <li key={label} className="flex items-center justify-between border-b border-[#1F3325] py-2 text-sm">
                        <span className="text-[#C8D4CB] flex items-center gap-2"><Check size={14} className="text-[#8DE038]"/>{label}</span>
                        <span className="font-display uppercase tracking-widest text-[#8DE038]">{price}</span>
                      </li>
                    ))}
                    {s.rows.length > 5 && (<li className="text-xs text-[#9CAEA3] pt-1">+ {s.rows.length - 5} more · see pricing</li>)}
                  </ul>
                  <div className="mt-7 flex gap-3">
                    <Link to={`/book?service=${s.slug}`} data-testid={`service-book-${s.slug}`} className="btn-primary text-xs">Book Now <ArrowRight size={14}/></Link>
                    <Link to="/pricing" className="btn-secondary text-xs">Full Pricing</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
